import React, { useState } from 'react';
import { Users, UserPlus, Shield, User, Mail, Calendar, Trash2, CheckCircle2, AlertCircle } from 'lucide-react';
import { ChurchMember } from '../../types';
import { MOCK_CHURCH_MEMBERS } from '../../mockData';
import { useAuth } from '../../context/AuthContext';

export const TeamManagement: React.FC = () => {
  const { currentTenant } = useAuth();
  const [members, setMembers] = useState<ChurchMember[]>(MOCK_CHURCH_MEMBERS);
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<'ADMIN' | 'USER'>('USER');
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const handleInvite = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !email.trim()) return;

    const newMember: ChurchMember = {
      id: `mem_${Date.now()}`,
      name: name.trim(),
      email: email.trim().toLowerCase(),
      role,
      status: 'INVITED',
      joinedAt: new Date().toISOString().split('T')[0],
    };

    setMembers((prev) => [newMember, ...prev]);
    setName('');
    setEmail('');
    setRole('USER');
    setIsInviteModalOpen(false);

    setSuccessMessage(`Invitation sent to ${newMember.email}`);
    setTimeout(() => setSuccessMessage(null), 4000);
  };

  const handleRoleToggle = (memberId: string) => {
    setMembers((prev) =>
      prev.map((m) => {
        if (m.id === memberId) {
          const newRole = m.role === 'ADMIN' ? 'USER' : 'ADMIN';
          return { ...m, role: newRole };
        }
        return m;
      })
    );
  };

  const handleRemoveMember = (memberId: string, memberName: string) => {
    if (confirm(`Are you sure you want to remove ${memberName} from this church organization?`)) {
      setMembers((prev) => prev.filter((m) => m.id !== memberId));
    }
  };

  return (
    <div className="space-y-8 w-full">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white tracking-tight flex items-center gap-2.5">
            <Users className="w-6 h-6 text-indigo-600 dark:text-indigo-400" />
            Church Team & Member Management
          </h1>
          <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">
            Manage staff and ministry volunteers with access to <span className="text-slate-900 dark:text-slate-200 font-semibold">{currentTenant?.name}</span>.
          </p>
        </div>

        <button
          onClick={() => setIsInviteModalOpen(true)}
          className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white font-semibold text-xs shadow-md transition-all self-start sm:self-auto cursor-pointer"
        >
          <UserPlus className="w-4 h-4" />
          <span>Invite Team Member</span>
        </button>
      </div>

      {/* Success Banner */}
      {successMessage && (
        <div className="p-4 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-800 dark:bg-emerald-500/10 dark:border-emerald-500/30 dark:text-emerald-300 flex items-center gap-2.5 text-xs animate-in fade-in transition-colors">
          <CheckCircle2 className="w-4 h-4 text-emerald-600 dark:text-emerald-400 flex-shrink-0" />
          <span>{successMessage}</span>
        </div>
      )}

      {/* Role Permissions Information Banner */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="p-4 rounded-xl bg-white border border-slate-200 shadow-sm dark:bg-slate-900/60 dark:border-slate-800 text-xs text-slate-700 dark:text-slate-300 space-y-1.5 transition-colors">
          <div className="flex items-center gap-2 font-bold text-amber-600 dark:text-amber-400">
            <Shield className="w-4 h-4" />
            <span>Church Admin Role</span>
          </div>
          <p className="text-slate-500 dark:text-slate-400 leading-relaxed">
            Can use the Voice Studio, access the church Audio Library, view credit balances, and invite or manage other team members within this church.
          </p>
        </div>

        <div className="p-4 rounded-xl bg-white border border-slate-200 shadow-sm dark:bg-slate-900/60 dark:border-slate-800 text-xs text-slate-700 dark:text-slate-300 space-y-1.5 transition-colors">
          <div className="flex items-center gap-2 font-bold text-indigo-600 dark:text-indigo-400">
            <User className="w-4 h-4" />
            <span>Church User Role (Staff & Volunteers)</span>
          </div>
          <p className="text-slate-500 dark:text-slate-400 leading-relaxed">
            Can generate scripture audio in the Voice Studio and stream or download audio files from the church library. Cannot manage users or view platform settings.
          </p>
        </div>
      </div>

      {/* Member Roster Card */}
      <div className="rounded-2xl bg-white border border-slate-200 shadow-sm dark:bg-slate-900/70 dark:border-slate-800 dark:shadow-xl overflow-hidden transition-colors">
        <div className="p-4 border-b border-slate-200 dark:border-slate-800/80 flex items-center justify-between">
          <div className="text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider">
            Team Members ({members.length})
          </div>
          <div className="text-xs text-slate-500">
            Organization: <strong className="text-slate-700 dark:text-slate-400">{currentTenant?.name}</strong>
          </div>
        </div>

        <div className="divide-y divide-slate-100 dark:divide-slate-800/80">
          {members.map((member) => (
            <div
              key={member.id}
              className="p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4 hover:bg-slate-50 dark:hover:bg-slate-800/30 transition-colors"
            >
              <div className="flex items-center gap-3.5">
                <div className="w-10 h-10 rounded-xl bg-indigo-50 border border-indigo-100 text-indigo-700 dark:bg-gradient-to-tr dark:from-indigo-600/30 dark:to-slate-800 dark:border-indigo-500/20 dark:text-indigo-300 flex items-center justify-center font-bold text-sm flex-shrink-0">
                  {member.name.charAt(0)}
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-sm text-slate-900 dark:text-white">{member.name}</span>
                    <span
                      className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded border ${
                        member.role === 'ADMIN'
                          ? 'bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/30'
                          : 'bg-indigo-500/10 text-indigo-700 dark:text-indigo-400 border-indigo-500/30'
                      }`}
                    >
                      {member.role === 'ADMIN' ? 'Church Admin' : 'Church User'}
                    </span>
                    <span
                      className={`text-[9px] font-medium px-2 py-0.5 rounded-full ${
                        member.status === 'ACTIVE'
                          ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-400'
                          : 'bg-amber-50 text-amber-700 dark:bg-amber-500/10 dark:text-amber-400'
                      }`}
                    >
                      {member.status === 'ACTIVE' ? 'Active' : 'Invited'}
                    </span>
                  </div>
                  <div className="flex items-center gap-3 text-xs text-slate-500 dark:text-slate-400 mt-1">
                    <span className="flex items-center gap-1">
                      <Mail className="w-3 h-3 text-slate-400" />
                      {member.email}
                    </span>
                    <span>•</span>
                    <span className="flex items-center gap-1">
                      <Calendar className="w-3 h-3 text-slate-400" />
                      Joined {member.joinedAt}
                    </span>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center gap-2 self-end sm:self-auto">
                <button
                  onClick={() => handleRoleToggle(member.id)}
                  className="px-3 py-1.5 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 border border-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 dark:text-slate-300 dark:border-slate-700 text-xs font-medium transition-colors cursor-pointer"
                  title="Toggle Admin / User role"
                >
                  Make {member.role === 'ADMIN' ? 'User' : 'Admin'}
                </button>
                <button
                  onClick={() => handleRemoveMember(member.id, member.name)}
                  className="p-1.5 rounded-lg bg-slate-100 hover:bg-rose-50 text-slate-400 hover:text-rose-600 border border-slate-200 dark:bg-slate-800 dark:hover:bg-rose-950 dark:hover:text-rose-400 dark:border-slate-700 transition-colors cursor-pointer"
                  title="Remove from team"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Invite Member Modal */}
      {isInviteModalOpen && (
        <div className="fixed inset-0 z-50 bg-slate-950/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white border border-slate-200 dark:bg-slate-900 dark:border-slate-800 rounded-2xl p-6 max-w-md w-full shadow-2xl space-y-5 animate-in fade-in zoom-in-95">
            <div className="flex items-center justify-between pb-3 border-b border-slate-200 dark:border-slate-800">
              <div className="flex items-center gap-2">
                <UserPlus className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                <h3 className="font-bold text-slate-900 dark:text-white text-base">Invite Church Team Member</h3>
              </div>
              <button
                onClick={() => setIsInviteModalOpen(false)}
                className="text-slate-400 hover:text-slate-600 dark:hover:text-white text-sm cursor-pointer"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleInvite} className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1.5">
                  Full Name
                </label>
                <input
                  type="text"
                  required
                  placeholder="e.g. Rachel Adams"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-2 text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-1 focus:ring-indigo-500 dark:bg-slate-800/80 dark:border-slate-700 dark:text-slate-100 dark:placeholder-slate-500"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1.5">
                  Email Address
                </label>
                <input
                  type="email"
                  required
                  placeholder="e.g. rachel@gracechurch.org"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-2 text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-1 focus:ring-indigo-500 dark:bg-slate-800/80 dark:border-slate-700 dark:text-slate-100 dark:placeholder-slate-500"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1.5">
                  Church Role
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => setRole('USER')}
                    className={`p-3 rounded-xl border text-left transition-all cursor-pointer ${
                      role === 'USER'
                        ? 'bg-indigo-50 border-indigo-500 text-indigo-900 shadow-sm dark:bg-indigo-600/20 dark:border-indigo-500 dark:text-white'
                        : 'bg-slate-50 border-slate-200 text-slate-600 hover:text-slate-900 dark:bg-slate-800/50 dark:border-slate-700 dark:text-slate-400 dark:hover:text-slate-200'
                    }`}
                  >
                    <div className="font-bold text-xs">Church User</div>
                    <div className="text-[10px] text-slate-500 dark:text-slate-400 mt-0.5">Voice Studio & Library</div>
                  </button>

                  <button
                    type="button"
                    onClick={() => setRole('ADMIN')}
                    className={`p-3 rounded-xl border text-left transition-all cursor-pointer ${
                      role === 'ADMIN'
                        ? 'bg-amber-50 border-amber-500 text-amber-900 shadow-sm dark:bg-amber-600/20 dark:border-amber-500 dark:text-white'
                        : 'bg-slate-50 border-slate-200 text-slate-600 hover:text-slate-900 dark:bg-slate-800/50 dark:border-slate-700 dark:text-slate-400 dark:hover:text-slate-200'
                    }`}
                  >
                    <div className="font-bold text-xs">Church Admin</div>
                    <div className="text-[10px] text-slate-500 dark:text-slate-400 mt-0.5">Team & Studio access</div>
                  </button>
                </div>
              </div>

              <div className="p-3 rounded-xl bg-slate-50 border border-slate-200 dark:bg-slate-800/50 dark:border-slate-700/60 text-[11px] text-slate-500 dark:text-slate-400 flex items-start gap-2">
                <AlertCircle className="w-3.5 h-3.5 text-indigo-500 dark:text-indigo-400 flex-shrink-0 mt-0.5" />
                <span>
                  The user will receive an activation email to join <strong className="text-slate-800 dark:text-slate-300">{currentTenant?.name}</strong>.
                </span>
              </div>

              <div className="flex items-center justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setIsInviteModalOpen(false)}
                  className="px-4 py-2 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 dark:bg-slate-800 dark:hover:bg-slate-700 dark:text-slate-300 text-xs font-semibold transition-colors cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-semibold shadow-md transition-colors cursor-pointer"
                >
                  Send Invitation
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
